import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_EVENT_TYPES, eventTypeFromToken, eventTypeSlug } from '../eventTypes';

const NO_EVENT_TYPES = 'none';

const FILTER_PARAMS = ['start', 'end', 'policy', 'types'];

export interface DateRange {
  start?: string;
  end?: string;
}

const isDefaultEventTypes = (types: Set<string>) =>
  types.size === DEFAULT_EVENT_TYPES.length && DEFAULT_EVENT_TYPES.every((type) => types.has(type));

const useTimelineParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Six months back. The end is tomorrow so the backend's date-only `lte` filter,
  // which lands on midnight, still includes everything from today.
  const defaultDateRange = useMemo<DateRange>(() => {
    const end = new Date();
    end.setDate(end.getDate() + 1);
    const start = new Date();
    start.setMonth(start.getMonth() - 6);

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, []);

  // Params holding a default are dropped, so the bare url stays clean.
  const write = (entries: [string, string | null][]) => {
    const next = new URLSearchParams(searchParams);

    entries.forEach(([name, value]) => {
      if (value) next.set(name, value);
      else next.delete(name);
    });

    setSearchParams(next, { replace: true });
  };

  const dateRange = useMemo<DateRange>(
    () => ({
      start: searchParams.get('start') || defaultDateRange.start,
      end: searchParams.get('end') || defaultDateRange.end
    }),
    [searchParams, defaultDateRange]
  );

  const sortOrder: 'asc' | 'desc' = searchParams.get('sort') === 'asc' ? 'asc' : 'desc';

  // An unrecognised token would otherwise filter the whole timeline away.
  const selectedEventTypes = useMemo(() => {
    if (searchParams.get('types') === NO_EVENT_TYPES) return new Set<string>();

    const requested = (searchParams.get('types') || '')
      .split(',')
      .map((token) => eventTypeFromToken(token.trim()))
      .filter(Boolean) as string[];

    return new Set(requested.length ? requested : DEFAULT_EVENT_TYPES);
  }, [searchParams]);

  return {
    dateRange,
    selectedPolicyId: searchParams.get('policy'),
    focusedEventId: searchParams.get('event'),
    sortOrder,
    selectedEventTypes,
    hasActiveFilters: FILTER_PARAMS.some((name) => searchParams.has(name)),

    setDateRange: (range: DateRange) =>
      write([
        ['start', range.start === defaultDateRange.start ? null : range.start || null],
        ['end', range.end === defaultDateRange.end ? null : range.end || null]
      ]),
    setSelectedPolicyId: (policyId: string | null) => write([['policy', policyId]]),
    setFocusedEventId: (eventId: string | null) => write([['event', eventId]]),
    setSortOrder: (order: 'asc' | 'desc') => write([['sort', order === 'desc' ? null : order]]),
    resetFilters: () => write(FILTER_PARAMS.map((name) => [name, null])),
    setSelectedEventTypes: (types: Set<string>) => {
      if (types.size === 0) return write([['types', NO_EVENT_TYPES]]);

      write([
        ['types', isDefaultEventTypes(types) ? null : [...types].map(eventTypeSlug).join(',')]
      ]);
    }
  };
};

export default useTimelineParams;
